import type { DataBuilderEstimatePostResponse } from './data-builder-api.types';
import type {
  RowLimitInstanceResult,
  RowLimitResultsByInstanceId,
  RowLimitSeverity,
} from './data-builder-row-limit.types';

// The `/estimate` endpoint returns one entry per deliverable keyed by the
// `name` we put in the request payload. We join back to instance IDs via a
// name → instanceId map captured at request time. Names are made unique by
// `buildDatasetInstanceLabelMap` (duplicates get a `(N)` suffix), so the join
// is reliable in the common case; duplicate user-renamed labels degrade to
// first-wins.

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const toFiniteNumber = (value: unknown): number | undefined => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return undefined;
  }
  return value;
};

const toSeverity = (status: unknown): RowLimitSeverity => {
  if (status === 'ROW_LIMIT') {
    return 'error';
  }
  return 'ok';
};

const extractEntries = (response: unknown): Record<string, unknown>[] => {
  if (isRecord(response) && Array.isArray(response.deliverables)) {
    return response.deliverables.filter(isRecord) as Record<string, unknown>[];
  }
  return [];
};

export const parseValidateRowLimitResponse = (
  response: DataBuilderEstimatePostResponse | unknown,
  nameToInstanceId: Record<string, string>
): RowLimitResultsByInstanceId => {
  const entries = extractEntries(response);
  const results: RowLimitResultsByInstanceId = {};

  for (const entry of entries) {
    const name = typeof entry.name === 'string' ? entry.name : undefined;
    if (name === undefined) {
      continue;
    }
    const instanceId = nameToInstanceId[name];
    if (instanceId === undefined || instanceId in results) {
      continue;
    }

    const rowCount = toFiniteNumber(entry.row_count);
    const rowLimit = toFiniteNumber(entry.row_count_limit);
    if (rowCount === undefined || rowLimit === undefined) {
      continue;
    }

    const description =
      typeof entry.row_count_description === 'string'
        ? entry.row_count_description
        : null;
    const statusDescription =
      typeof entry.status_description === 'string'
        ? entry.status_description
        : null;

    const result: RowLimitInstanceResult = {
      rowCount,
      rowLimit,
      description,
      statusDescription,
      severity: toSeverity(entry.status),
    };
    results[instanceId] = result;
  }

  return results;
};
